import React, { useEffect, useState } from 'react';
import {
  CardHcd,
  ReferringPractitioner,
  UserPatientProfile,
} from '@maiia/model/generated/model/api-patient/api-patient';
import { asyncActions } from '@docavenue/core';
import { useCurrentUser } from '@docavenue/components';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import { Button, DialogActions, DialogContent, Divider } from '@mui/material';
import {
  IconHeart,
  IconHeartFilled,
  IconStar,
  IconStarFilled,
} from '@maiia/design-system';
import IconSwitcher from '../../atoms/IconSwitcher/IconSwitcher';
import {
  authActions,
  favoritePractitionersActions,
  usersActions,
} from '@/src/actions';
import ConfirmDialog from '../Dialogs/ConfirmDialog';
import { getReferringPractitionerFullName } from '../../utils/patient';
import { getCardFullName } from '../../utils';
import PatientNameTag from '@/components/molecules/PatientNameTag/PatientNameTag';
import { useTranslation } from '@/src/i18n';
import { NONE } from '@/src/constants';

interface Props {
  account: UserPatientProfile | undefined;
  practitionerId: string;
  centerId: string;
  onClose: Function;
  card: CardHcd;
}

const REFERRING = 'REFERRING';
const FAVORITE = 'FAVORITE';

type MiniProfile = {
  profileId: string;
  firstName: string;
  type: 'REFERRING' | 'FAVORITE' | 'NONE';
  referringPractitioner: ReferringPractitioner;
};

const FavoriteSelector: React.FC<Props> = ({
  practitionerId,
  centerId,
  onClose,
  card,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const [{ values }, setState] = useState<{
    values: MiniProfile[];
    initialValues: MiniProfile[];
  }>({
    values: [],
    initialValues: [],
  });
  const setValues = items => setState(prev => ({ ...prev, values: items }));

  useEffect(() => {
    asyncActions(
      dispatch,
      favoritePractitionersActions.getList({ practitionerId, centerId }),
    ).then(res => setState({ values: res, initialValues: res }));
  }, []);

  return (
    <>
      <DialogContent sx={{ display: 'flex', justifyContent: 'center' }}>
        <div className="wrapper-favorites">
          <p>
            {practitionerId
              ? t('help_favorite_doctor')
              : t('help_favorite_center')}
          </p>
          <ul className="favorite-selector">
            {values.map((profile, index) => {
              const { profileId: id, type, referringPractitioner } = profile;

              return (
                <>
                  <li
                    key={id}
                    className={clsx(
                      'favorite-selector__item',
                      !card?.professional?.cpsId && 'only-favorite',
                    )}
                  >
                    <PatientNameTag
                      profile={profile}
                      profileIndex={index}
                      nameVariant="first"
                      avatarSize="lg"
                      vertical
                    />

                    <div className="switchers">
                      <div
                        className="favorite"
                        onClick={() => {
                          const newValues = values.map(v => {
                            if (v.profileId !== id) return v;
                            return {
                              ...v,
                              type: type === FAVORITE ? NONE : FAVORITE,
                            };
                          });
                          setValues(newValues);
                        }}
                        data-testid={`favorite-physician-relative-${index}`}
                      >
                        <IconSwitcher
                          className="favorite"
                          on={type === FAVORITE}
                          onClick={() => null}
                          onIcon={<IconStarFilled />}
                          offIcon={<IconStar />}
                        />

                        <span>{t('add_to_favorites')}</span>
                      </div>

                      {!!card?.professional?.cpsId && (
                        <div
                          className="referring"
                          data-testid={`attending-physician-relative-${index}`}
                          onClick={async () => {
                            if (
                              referringPractitioner &&
                              referringPractitioner?.practitionerId !==
                                card?.professional?.practitionerId &&
                              type !== REFERRING
                            ) {
                              const canProceed = await ConfirmDialog({
                                title: t('change_referring_practitioner_title'),
                                message: t(
                                  'change_referring_practitioner_content',
                                  {
                                    currentPractitionerName: getReferringPractitionerFullName(
                                      { referringPractitioner },
                                    ),
                                    newName: getCardFullName(t, card),
                                  },
                                ),
                                t,
                              });
                              if (!canProceed) return;
                            }

                            const newValues = values.map(v => {
                              if (v.profileId !== id) return v;
                              return {
                                ...v,
                                type: type === REFERRING ? NONE : REFERRING,
                              };
                            });
                            setValues(newValues);
                          }}
                        >
                          <IconSwitcher
                            className="referring"
                            on={type === REFERRING}
                            onIcon={<IconHeartFilled />}
                            offIcon={<IconHeart />}
                            onClick={() => null}
                          />
                          <span>{t('referring_doctor')}</span>
                        </div>
                      )}
                    </div>
                  </li>
                  {index !== values.length - 1 && <Divider sx={{ my: 3 }} />}
                </>
              );
            })}
          </ul>
        </div>
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          data-testid="validate-btn"
          variant="contained"
          color="secondary"
          onClick={async () => {
            await asyncActions(
              dispatch,
              favoritePractitionersActions.updateManyPost(values, {
                params: {
                  practitionerId,
                  centerId,
                },
              }),
            );
            const user = await asyncActions(
              dispatch,
              usersActions.getOne('me'),
            );
            dispatch(
              authActions.setItem({
                ...currentUser,
                ...user,
              }),
            );
            onClose();
          }}
        >
          {t('common:action_validate')}
        </Button>
      </DialogActions>
    </>
  );
};

export default FavoriteSelector;
